import type { Dimensions } from "../canvas/canvasTypes";
import { renderComicObject, renderPanelBoundaries } from "../comic/ComicRenderer";
import type { ComicBubbleObject, ComicPanel } from "../comic/comicTypes";
import type { PersistedLayerState } from "./layerTypes";
import type { LayerRasterStore } from "./LayerRasterStore";

export interface CompositeLayerDocument {
  layers: ReadonlyArray<PersistedLayerState>;
  objects: ReadonlyArray<ComicBubbleObject>;
  panels: ReadonlyArray<ComicPanel>;
}

/** The one ordering path used by display, thumbnails, export and layer merge previews. */
export function compositeLayerDocument(
  context: CanvasRenderingContext2D,
  raster: LayerRasterStore,
  state: CompositeLayerDocument,
  size: Dimensions,
  includePanelFrames = true,
  rasterOverrides?: ReadonlyMap<string, CanvasImageSource>,
): void {
  for (const layer of [...state.layers].sort((left, right) => left.order - right.order)) {
    if (!layer.visible || layer.opacity <= 0) continue;
    context.save();
    context.globalAlpha = layer.opacity;
    if (layer.type === "raster") {
      const canvas = rasterOverrides?.get(layer.id) ?? raster.get(layer.id)?.getCanvas();
      if (canvas) context.drawImage(canvas, 0, 0);
    } else {
      for (const object of [...state.objects]
        .filter((candidate) => candidate.layerId === layer.id)
        .sort((left, right) => left.order - right.order)) {
        const panel = state.panels.find((candidate) => candidate.id === object.panelId);
        if (!panel) continue;
        context.save();
        context.beginPath();
        context.rect(panel.x, panel.y, panel.width, panel.height);
        context.clip();
        renderComicObject(context, object);
        context.restore();
      }
    }
    context.restore();
  }
  if (includePanelFrames && state.panels.length > 0) {
    renderPanelBoundaries(context, state.panels, size.width, size.height);
  }
}

export function createCompositeCanvas(
  raster: LayerRasterStore,
  state: CompositeLayerDocument,
  size: Dimensions,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D is unavailable for document compositing.");
  compositeLayerDocument(context, raster, state, size);
  return canvas;
}
